import currencyFormatter from "currency-formatter";

/**
 * Returns the currency symbol for a given ISO 4217 currency code.
 *
 * Uses the same `currency-formatter` package and identical call as
 * WebV5's `onlyGetCurrencySymbolForGivenCurrencyCode` in CurrencySymbolHandler.js:
 *   currencyFormatter.format(0, { code, thousand: ',', decimal: ',', precision: 2, format: '%s' }).replace("0,00", "")
 *
 * @example getCurrencySymbol("USD") // "$"
 * @example getCurrencySymbol("EUR") // "€"
 * @example getCurrencySymbol("AED") // "د.إ.‏"
 */
export function getCurrencySymbol(currencyCode?: string | null): string {
    const code = currencyCode || "USD";
    return currencyFormatter.format(0, { code, thousand: ",", decimal: ",", precision: 2, format: "%s" }).replace("0,00", "");
}

/**
 * Formats a numeric amount using the given ISO 4217 currency code.
 * Falls back to USD when the currency code is absent.
 *
 * Mirrors WebV5 ExpenseApprovals `formatMoney` exactly:
 *   `${onlyGetCurrencySymbolForGivenCurrencyCode(currency)} ${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
 *
 * - Symbol resolved via the same `currency-formatter` package used in WebV5.
 * - Number formatted with the browser's locale (toLocaleString with undefined locale).
 *
 * @example formatCurrency(1234.5, "EUR")      // "€ 1,234.50"
 * @example formatCurrency(28.4,   "AED")      // "د.إ.‏ 28.40"
 * @example formatCurrency(1234.5, "USD", "") // "$1,234.50"
 */
export function formatCurrency(amount: number, currencyCode?: string | null, separator = " "): string {
    const symbol = getCurrencySymbol(currencyCode);
    const formattedNumber = amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${symbol}${separator}${formattedNumber}`;
}
